<app-slide-panel
    variant="full"
    [width]="'800'"
    [zIndex]="900"
    [flushBody]="true"
    [titleIcon]="faCloudArrowDown"
    [title]="'cloudImport.panel.title' | translate"
    (closed)="close()">

  <!-- Body -->
  <div class="panel-body">
    @switch (view()) {

      <!-- ── Connect: provider buttons + own connected accounts ─────── -->
      @case ('connect') {
        <div class="connect-view">
          @if (connectionsLoading()) {
            <div class="loading-state">
              <p-progressSpinner [style]="{ width: '36px', height: '36px' }" strokeWidth="3"></p-progressSpinner>
            </div>
          } @else {
            <p class="connect-intro">{{ 'cloudImport.connect.intro' | translate }}</p>

            @if (enabledProviders().length === 0) {
              <div class="empty-state">
                <fa-duotone-icon [icon]="faCloudArrowDown" class="empty-icon"></fa-duotone-icon>
                <p>{{ 'cloudImport.connect.noProviders' | translate }}</p>
              </div>
            } @else {
              <!-- Centered provider tiles (Notion/Canva importer pattern — Robert, 2026-07-03).
                   Visual = mark + name; the ACTION lives in the aria-label ("Connect Google Drive"). -->
              <div class="provider-tiles">
                @for (provider of enabledProviders(); track provider) {
                  <button type="button"
                          class="provider-tile"
                          [disabled]="connecting()"
                          [attr.aria-label]="'cloudImport.connect.connectTo' | translate:{ provider: providerName(provider) }"
                          (click)="onConnect(provider)">
                    <fa-icon [icon]="providerMark(provider)" class="provider-mark"></fa-icon>
                    <span class="tile-name">{{ providerName(provider) }}</span>
                  </button>
                }
              </div>
              <!-- In-product data-use notice: shown at the pre-consent moment, as the Google
                   API Services User Data Policy requires ("timely and shown in context"). -->
              <p class="privacy-note field-hint">
                {{ 'cloudImport.connect.privacyNote' | translate }}
                <a [href]="privacyUrl" target="_blank" rel="noopener">{{ 'cloudImport.connect.privacyNoteLink' | translate }}</a>.
              </p>
            }

            @if (connections().length > 0) {
              <div class="section-label">{{ 'cloudImport.connect.yourAccounts' | translate }}</div>
              <div class="account-list">
                @for (connection of connections(); track connection.id) {
                  <div class="account-row">
                    <fa-icon [icon]="providerMark(connection.provider)" class="provider-mark"></fa-icon>
                    <div class="account-info">
                      <span class="name">{{ providerName(connection.provider) }}</span>
                      <span class="email">{{ connection.accountLabel }}</span>
                    </div>
                    <button pButton
                            [outlined]="true"
                            severity="danger"
                            size="small"
                            (click)="onDisconnect(connection)">
                      {{ 'cloudImport.connect.disconnect' | translate }}
                    </button>
                  </div>
                }
              </div>
              <p class="disconnect-hint">{{ 'cloudImport.connect.disconnectHint' | translate }}</p>
              <button pButton [text]="true" size="small" class="back-btn" (click)="backToBrowse()">
                {{ 'cloudImport.connect.backToBrowse' | translate }}
              </button>
            }
          }
        </div>
      }

      <!-- ── Browse: account picker + lazy tree + checkbox rows ─────────────── -->
      @case ('browse') {
        <div class="browse-view">
          <div class="account-bar">
            <p-select
              [options]="connectionOptions()"
              optionLabel="label"
              optionValue="value"
              [ngModel]="selectedConnectionId()"
              (ngModelChange)="onConnectionPicked($event)"
              styleClass="account-select"
              appendTo="body">
            </p-select>
            <button pButton [text]="true" size="small" (click)="showConnectView()">
              {{ 'cloudImport.browse.manageAccounts' | translate }}
            </button>
          </div>

          <div class="browse-split">
            <!-- Left: folder tree -->
            <div class="folder-tree">
              <ng-container *ngTemplateOutlet="treeTpl; context: { $implicit: folderTree(), level: 0 }"></ng-container>
            </div>

            <!-- Right: current folder rows (five-state chain) -->
            <div class="file-content">
              <div class="content-header">
                <div class="breadcrumbs">
                  @for (crumb of breadcrumbs(); track crumb.id ?? '__root__'; let last = $last) {
                    @if (!last) {
                      <button class="crumb-btn" (click)="onBreadcrumbClick(crumb)">{{ crumb.name }}</button>
                      <span class="crumb-sep">/</span>
                    } @else {
                      <span class="crumb-current">{{ crumb.name }}</span>
                    }
                  }
                </div>
              </div>

              <div class="content-area">
                @if (browseState() === 'loading') {
                  <div class="loading-state">
                    <p-progressSpinner [style]="{ width: '36px', height: '36px' }" strokeWidth="3"></p-progressSpinner>
                  </div>
                } @else if (browseState() === 'revoked') {
                  <div class="empty-state">
                    <fa-duotone-icon [icon]="faLinkSlash" class="empty-icon"></fa-duotone-icon>
                    <p>{{ 'cloudImport.browse.accessExpired' | translate }}</p>
                    <button pButton severity="secondary" [outlined]="true" size="small" (click)="showConnectView()">
                      {{ 'cloudImport.browse.reconnect' | translate }}
                    </button>
                  </div>
                } @else if (browseState() === 'error') {
                  <!-- The browse read failed: the canonical section-grain face — a failed read never wears the
                       empty-state block -->
                  <app-load-failed [compact]="true" [message]="'cloudImport.browse.loadFailed' | translate" (retry)="retryBrowse()"/>
                } @else if (entries().length === 0) {
                  <div class="empty-state">
                    <fa-duotone-icon [icon]="faFolderOpen" class="empty-icon"></fa-duotone-icon>
                    <p>{{ 'cloudImport.browse.emptyFolder' | translate }}</p>
                  </div>
                } @else {
                  <!-- Lean rows: checkbox + icon + primary text + meta (member-search row anatomy) -->
                  <div class="entry-list">
                    @for (entry of entries(); track entry.id) {
                      <div class="entry-row" [class.selected]="isSelected(entry)" (click)="onRowClick(entry)">
                        <p-checkbox
                          [binary]="true"
                          [ngModel]="isSelected(entry)"
                          (onChange)="toggleEntry(entry)"
                          (click)="$event.stopPropagation()">
                        </p-checkbox>
                        <fa-duotone-icon [icon]="entryIcon(entry)" class="entry-icon" [fixedWidth]="true"></fa-duotone-icon>
                        <div class="entry-info">
                          <span class="name">{{ entry.name }}</span>
                          @if (entrySize(entry)) {
                            <span class="meta">{{ entrySize(entry) }}</span>
                          } @else if (entry.isFolder) {
                            <span class="meta">{{ 'cloudImport.browse.folder' | translate }}</span>
                          }
                        </div>
                        @if (entry.isFolder) {
                          <fa-duotone-icon [icon]="faChevronRight" class="entry-open" [fixedWidth]="true"></fa-duotone-icon>
                        }
                      </div>
                    }
                  </div>
                }
              </div>
            </div>
          </div>
        </div>
      }

      <!-- ── Started: live progress, closable anytime ──────────────── -->
      @case ('started') {
        @if (startedRun(); as run) {
          <div class="started-view">
            @if (startedIsActive()) {
              <fa-duotone-icon [icon]="faCloudArrowDown" class="started-icon"></fa-duotone-icon>
              <h3 class="started-title">{{ 'cloudImport.started.title' | translate }}</h3>
              <p-progressBar
                [value]="startedProgress()"
                [showValue]="false"
                [style]="{ height: '6px', width: '100%' }">
              </p-progressBar>
              <p class="started-counts">
                {{ 'cloudImport.started.progress' | translate:{ done: run.importedFiles, total: run.totalFiles } }}
              </p>
              <p class="started-hint">{{ 'cloudImport.started.closeHint' | translate }}</p>
              <button pButton severity="danger" [text]="true" size="small" (click)="cancelStartedRun()">
                {{ 'cloudImport.started.cancel' | translate }}
              </button>
            } @else {
              <fa-duotone-icon
                [icon]="run.status === 'COMPLETED' ? faCircleCheck : faCircleExclamation"
                class="started-icon"
                [class.success]="run.status === 'COMPLETED'">
              </fa-duotone-icon>
              <h3 class="started-title">{{ 'cloudImport.started.status.' + run.status | translate }}</h3>
              @if (run.failedFiles > 0 || run.skippedFiles > 0) {
                <p class="started-counts">
                  {{ 'cloudImport.started.partialSummary' | translate:{ imported: run.importedFiles, total: run.totalFiles } }}
                </p>
              } @else if (run.status === 'COMPLETED') {
                <p class="started-counts">
                  {{ 'cloudImport.started.completedSummary' | translate:{ imported: run.importedFiles, total: run.totalFiles } }}
                </p>
              }
            }
          </div>
        }
      }
    }
  </div>

  <!-- Footer -->
  <div panel-footer class="picker-footer">
    @if (view() === 'browse') {
      <div class="footer-context">
        @if (context(); as ctx) {
          <span class="footer-destination">
            {{ 'cloudImport.footer.importingInto' | translate:{ target: ctx.destinationLabel } }}
          </span>
          @if (ctx.destinationIsGallery) {
            <span class="footer-note">{{ 'cloudImport.footer.mediaOnlyNote' | translate }}</span>
          }
        }
        @if (selectionCount() > 0) {
          <span class="selection-count">
            {{ 'cloudImport.footer.selected' | translate:{ count: selectionCount() } }}
            @if (selectionHasFolder()) {
              {{ 'cloudImport.footer.foldersIncludeContents' | translate }}
            }
          </span>
        }
      </div>
      <button class="add-btn"
              [disabled]="selectionCount() === 0 || starting()"
              (click)="startImport()">
        @if (starting()) {
          {{ 'cloudImport.footer.starting' | translate }}
        } @else {
          {{ 'cloudImport.footer.startImport' | translate }}
        }
      </button>
    } @else if (view() === 'started') {
      <button class="add-btn" (click)="close()">
        {{ 'common.close' | translate }}
      </button>
    }
  </div>
</app-slide-panel>

<!-- Recursive tree template (drive-picker clone) -->
<ng-template #treeTpl let-nodes let-level="level">
  @for (node of nodes; track node.id ?? '__root__') {
    <div class="tree-node focus-ring"
         [class.active]="isTreeNodeActive(node)"
         [style.padding-left.px]="8 + level * 16"
         role="button" tabindex="0" [attr.aria-label]="node.label"
         [attr.aria-expanded]="node.childrenLoaded && node.children.length === 0 ? null : node.expanded"
         (click)="onTreeNodeClick(node)" (keydown.enter)="onTreeNodeClick(node)">
      @if (node.loading) {
        <fa-duotone-icon [icon]="faSpinnerThird" class="chevron-icon fa-spin" [fixedWidth]="true"></fa-duotone-icon>
      } @else if (node.childrenLoaded && node.children.length === 0) {
        <span class="chevron-spacer"></span>
      } @else {
        <fa-duotone-icon
          [icon]="node.expanded ? faChevronDown : faChevronRight"
          class="chevron-icon"
          [fixedWidth]="true">
        </fa-duotone-icon>
      }
      <fa-duotone-icon [icon]="node.isRoot ? faCloudArrowDown : faFolder" class="folder-icon" [fixedWidth]="true"></fa-duotone-icon>
      <span class="node-name">{{ node.label }}</span>
    </div>
    @if (node.expanded && node.children.length > 0) {
      <ng-container *ngTemplateOutlet="treeTpl; context: { $implicit: node.children, level: level + 1 }"></ng-container>
    }
  }
</ng-template>
